import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';

const formatAmount = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const CommissionsManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [filters, setFilters] = useState<{ affiliate_id: string; status: '' | 'pending' | 'approved' | 'paid' | 'void'; from: string; to: string; limit: number }>({ affiliate_id: '', status: '', from: '', to: '', limit: 50 });

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.listCommissions({
        affiliate_id: filters.affiliate_id || undefined,
        status: (filters.status || undefined) as any,
        from: filters.from || undefined,
        to: filters.to || undefined,
        limit: filters.limit,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const updateStatus = async (id: string, status: 'pending' | 'approved' | 'paid' | 'void') => {
    setLoading(true);
    try {
      await adminService.updateCommissionStatus(id, status);
      toast.success('Status updated');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Commissions</h2>
      <div className="bg-white p-4 rounded shadow mb-4 grid md:grid-cols-6 gap-3">
        <input className="border rounded px-3 py-2" placeholder="Affiliate ID" value={filters.affiliate_id} onChange={(e) => setFilters({ ...filters, affiliate_id: e.target.value })} />
        <select className="border rounded px-3 py-2" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="void">Void</option>
        </select>
        <input type="date" className="border rounded px-3 py-2" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        <input type="date" className="border rounded px-3 py-2" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        <input type="number" className="border rounded px-3 py-2" value={filters.limit} min={1} max={500} onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })} />
        <button onClick={load} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">{loading ? 'Loading...' : 'Apply Filters'}</button>
      </div>

      <div className="overflow-x-auto bg-white shadow rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2">{c.id}</td>
                <td className="px-4 py-2">{c.affiliate_id}</td>
                <td className="px-4 py-2">{formatAmount(c.amount)}</td>
                <td className="px-4 py-2">{c.currency || 'USD'}</td>
                <td className="px-4 py-2 capitalize">{c.status}</td>
                <td className="px-4 py-2">{new Date(c.created_at || c.createdAt || Date.now()).toLocaleString()}</td>
                <td className="px-4 py-2 space-x-2 text-right">
                  <button onClick={() => updateStatus(c.id, 'approved')} className="px-3 py-1 bg-yellow-600 text-white rounded">Approve</button>
                  <button onClick={() => updateStatus(c.id, 'paid')} className="px-3 py-1 bg-green-600 text-white rounded">Mark Paid</button>
                  <button onClick={() => updateStatus(c.id, 'void')} className="px-3 py-1 bg-gray-600 text-white rounded">Void</button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center text-gray-500">No commissions</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommissionsManagement;
